import { useState, useEffect, useRef } from 'react'
import { Search, LogOut, User as UserIcon, Moon, Sun, Monitor, Keyboard } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useUIStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { NotificationBell } from '@/components/NotificationPanel'
import { AIAlertCenter } from '@/components/ai/AIAlertCenter'
import { TasksPanel } from '@/components/tasks/TasksPanel'
import { useNavigate } from 'react-router-dom'
import { cn, initials } from '@/lib/utils'
import { get } from '@/lib/api'
import { LanguageSelector } from '@/components/ui/LanguageSelector'
import { useTheme } from '@/hooks/useDarkMode'

interface HealthResp { status: 'healthy' | 'degraded' | 'down' }

export function TopBar() {
  const { t } = useTranslation()
  const open = useUIStore((s) => s.openCommandPalette)
  const openShortcutsHelp = useUIStore((s) => s.openShortcutsHelp)
  const { theme, cycleTheme } = useTheme()
  const user = useAuthStore((s) => s.user)
  const clear = useAuthStore((s) => s.clear)
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const { data: health } = useQuery({
    queryKey: ['telephony', 'health-pill'],
    queryFn: () => get<HealthResp>('/api/v1/telephony/health', { background: true }),
    refetchInterval: 30_000,
    staleTime: 10_000,
  })

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    if (menuOpen) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  function signOut() {
    clear()
    navigate('/login')
  }

  const userName = user ? `${user.firstName} ${user.lastName}` : ''

  const omniState = health?.status
  const omniOnline = omniState === 'healthy'
  const omniOffline = omniState === 'down'

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card/95 px-4 shadow-[0_1px_2px_rgba(7,24,46,0.03)] backdrop-blur md:px-6">
      {/* Search */}
      <button
        onClick={open}
        className="hidden w-[300px] items-center gap-2.5 rounded-lg border border-border bg-page px-3 py-2 text-[13px] text-muted shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] transition-colors hover:border-[rgba(79,140,255,0.35)] hover:bg-card focus:border-[var(--vela-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--vela-focus-ring)] sm:flex"
        aria-label="Open command palette"
      >
        <Search className="h-4 w-4 shrink-0 text-muted" />
        <span className="flex-1 text-left">{t('common.search')}</span>
        <kbd className="inline-flex items-center rounded border border-border bg-card px-1.5 py-px font-sans text-[10px] text-muted shadow-sm">
          ⌘K
        </kbd>
      </button>

      <button
        onClick={open}
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-page text-muted transition-colors hover:bg-card hover:text-heading sm:hidden"
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </button>

      <div className="flex items-center gap-1.5 rounded-full border border-border bg-card px-1.5 py-1 shadow-[0_1px_2px_rgba(7,24,46,0.04)]">
        {/* Omni status pill */}
        <span
          className={cn(
            'hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-medium md:inline-flex',
            omniOffline ? 'border-neg/15 bg-neg-soft text-neg' : omniOnline ? 'border-pos/15 bg-pos-soft text-pos' : 'border-border bg-page text-muted',
          )}
        >
          <span
            aria-hidden
            className={cn('h-1.5 w-1.5 rounded-full', omniOffline ? 'bg-neg' : omniOnline ? 'bg-pos' : 'bg-ink-faint')}
          />
          {omniOffline ? t('common.omniOffline') : omniOnline ? t('common.omniOnline') : t('common.omniUnknown')}
        </span>

        <Divider />

        <LanguageSelector />

        <Divider />

        <button
          onClick={cycleTheme}
          title={
            theme === 'light' ? 'Switch to dark mode'
              : theme === 'dark' ? 'Switch to system theme'
              : 'Switch to light mode'
          }
          aria-label="Toggle theme"
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-page hover:text-heading"
        >
          {theme === 'light' ? <Moon size={16} /> : theme === 'dark' ? <Sun size={16} /> : <Monitor size={16} />}
        </button>

        <button
          onClick={openShortcutsHelp}
          title="Keyboard shortcuts (?)"
          aria-label="Keyboard shortcuts"
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-page hover:text-heading"
        >
          <Keyboard size={16} />
        </button>

        <Divider />

        <AIAlertCenter />

        <NotificationBell />

        <Divider />

        <TasksPanel />

        {user ? (
          <>
            <Divider />
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full bg-[var(--vela-primary)] text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(79,140,255,0.24)] transition-shadow hover:ring-2 hover:ring-[var(--vela-focus-ring)]',
                  menuOpen && 'ring-2 ring-[var(--vela-focus-ring)]',
                )}
                aria-label="Account menu"
                aria-expanded={menuOpen}
              >
                {initials(userName)}
              </button>

              {menuOpen ? (
                <div
                  className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-border bg-card shadow-pop"
                  role="menu"
                >
                  <div className="border-b border-border px-3.5 py-3">
                    <div className="text-[13px] font-semibold text-heading">{userName}</div>
                    <div className="mt-0.5 truncate text-[11px] text-muted">{user.email}</div>
                  </div>
                  <div className="p-1">
                    <MenuItem
                      icon={<UserIcon className="h-3.5 w-3.5" />}
                      label={t('auth.profileSecurity')}
                      onClick={() => { setMenuOpen(false); navigate('/profile') }}
                    />
                    <MenuItem
                      icon={<LogOut className="h-3.5 w-3.5" />}
                      label={t('auth.signOut')}
                      onClick={signOut}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </header>
  )
}

function Divider() {
  return <span aria-hidden className="hidden h-5 w-px self-center bg-border sm:block" />
}

function MenuItem({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] text-body transition-colors hover:bg-page hover:text-heading"
      role="menuitem"
    >
      {icon}
      {label}
    </button>
  )
}
